/** @jsxImportSource @emotion/react */
import Dropdown from 'components/atoms/Dropdown'
import Pagination from 'components/atoms/Pagination'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import { BillingLoading } from 'components/lab/billingEraReports/BillingLoading'
import { NotesIndicator } from 'components/lab/billingNotes/BillingNotes'
import routes from 'components/lab/constants/routes'
import { formatCalendarDate, formatEasternDateTime } from 'helpers/dates'
import { useAppSelector } from 'hooks/redux'
import React from 'react'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useGetNeedsWorkListQuery } from 'store/services/needsWork'
import { NeedsWorkListItem, NeedsWorkListQuery, NeedsWorkReason, NeedsWorkStatus } from 'store/types'
import { useDebounce } from 'use-debounce'
import {
  FOLLOW_UP_COLORS,
  flaggedFor,
  followUpState,
  localDay,
  NEEDS_WORK_REASON_OPTIONS,
  REMIT_RESOLUTION_LABELS,
} from './needsWork'
import { OwnerOption, OwnerSelect } from './OwnerSelect'
import { useNeedsWorkActions } from './useNeedsWorkActions'

// One row of equally tall controls, wrapping to three, then two columns on
// narrower screens.
const filtersCss = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(200px, 1.5fr) minmax(180px, 1.3fr) minmax(160px, 1.1fr) minmax(150px, 1fr) minmax(150px, 1fr)',
  gap: 16,
  alignItems: 'end',
  '@media (max-width: 1400px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
  '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
}

const labelCss = { display: 'block', fontSize: 13, fontWeight: 600, color: '#6c757d', marginBottom: 6 }

type StatusView = 'OPEN' | 'DUE' | 'RESOLVED'

const VIEW_COUNT_LABELS: Record<StatusView, string> = {
  OPEN: 'open',
  DUE: 'due or overdue',
  RESOLVED: 'resolved',
}

// The same choices every other table offers.
const PAGE_SIZE_OPTIONS = [{ label: '12' }, { label: '20' }, { label: '50' }, { label: '100' }, { label: '200' }]

const chargeUrl = (item: NeedsWorkListItem): string | null => (item.patientId
  ? `${routes.index}${routes.patient.root}/${item.patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${item.chargeId}`
  : null)

const money = (value?: number | null): string => `$${Number(value ?? 0).toFixed(2)}`

type RowProps = {
  item: NeedsWorkListItem
  actions: ReturnType<typeof useNeedsWorkActions>
}

const Row: React.FC<RowProps> = ({ item, actions }) => {
  const url = chargeUrl(item)
  const due = item.status === 'OPEN' ? followUpState(item.followUpDate) : null
  const age = flaggedFor(item.createdAt)

  return (
    <tr>
      <td>
        <div css={{ fontWeight: 600 }}>{item.patientName || '—'}</div>
        {item.patientDob && <div className="text-muted" css={{ fontSize: 12 }}>DOB {formatCalendarDate(item.patientDob)}</div>}
      </td>
      <td>
        {url ? (
          <Link to={url} target="_blank" rel="noopener noreferrer" css={{ fontWeight: 600 }}>
            {item.chargeId.slice(0, 8)}
          </Link>
        ) : (
          item.chargeId.slice(0, 8)
        )}
        <div className="text-muted" css={{ fontSize: 12 }}>
          {[
            item.orderId ? `Order ${item.orderId}` : null,
            item.dateOfService ? `DOS ${formatCalendarDate(item.dateOfService)}` : null,
            item.payerName,
          ].filter(Boolean).join(' · ')}
        </div>
      </td>
      <td>
        <div>{item.reasonLabel}</div>
        {item.remit?.parked && (
          <span
            className="badge badge-warning"
            title="A remit is parked with this item — nothing is posted until it is resolved">
            Parked remit · paid {money(item.remit.paidAmount)}
          </span>
        )}
        {item.status !== 'OPEN' && item.remitResolution && (
          <div className="text-muted" css={{ fontSize: 12 }}>{REMIT_RESOLUTION_LABELS[item.remitResolution]}</div>
        )}
      </td>
      <td>{item.owner?.name || '—'}</td>
      {item.status === 'OPEN' ? (
        <>
          <td css={{ color: due ? FOLLOW_UP_COLORS[due] : undefined, fontWeight: due === 'OVERDUE' ? 600 : undefined }}>
            {formatCalendarDate(item.followUpDate, '—')}
            {due === 'OVERDUE' && <div css={{ fontSize: 12 }}>overdue</div>}
            {due === 'TODAY' && <div css={{ fontSize: 12 }}>today</div>}
          </td>
          <td>
            {age === 'today' ? 'Today' : age}
            <div className="text-muted" css={{ fontSize: 12 }}>by {item.createdBy?.name || 'the system'}</div>
          </td>
        </>
      ) : (
        <>
          <td>
            {formatEasternDateTime(item.resolvedAt, '—')}
            <div className="text-muted" css={{ fontSize: 12 }}>by {item.resolvedBy?.name || '—'}</div>
          </td>
          <td css={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.resolutionNote || '—'}</td>
        </>
      )}
      <td css={{ whiteSpace: 'nowrap' }}>
        <NotesIndicator count={item.notesCount} isOpen={false} onToggle={() => actions.notes(item)} />
        {item.status === 'OPEN' && actions.canModify && (
          <Button size="sm" variant="outline-secondary" className="ml-2" onClick={() => actions.edit(item)}>
            Edit
          </Button>
        )}
        {item.status === 'OPEN' && actions.canResolve(item) && (
          <Button size="sm" variant="success" className="ml-2" onClick={() => actions.resolve(item)}>
            Resolve
          </Button>
        )}
      </td>
    </tr>
  )
}

const NeedsWorkList = () => {
  const currentUserId = useAppSelector((state) => state.init.id)
  const actions = useNeedsWorkActions()
  // "Due or overdue" is open items whose follow-up date has arrived.
  const [view, setView] = React.useState<StatusView>('OPEN')
  const status: NeedsWorkStatus = view === 'RESOLVED' ? 'RESOLVED' : 'OPEN'
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(20)
  const [search, setSearch] = React.useState('')
  const [reason, setReason] = React.useState<NeedsWorkReason | ''>('')
  const [owner, setOwner] = React.useState<OwnerOption | null>(null)
  const [followUpFrom, setFollowUpFrom] = React.useState('')
  const [followUpTo, setFollowUpTo] = React.useState('')
  const [debouncedSearch] = useDebounce(search, 300)

  const ownerId = owner?.value
  const me: OwnerOption[] = currentUserId ? [{ value: Number(currentUserId), label: 'Me' }] : []
  const filtered = Boolean(search || owner || reason || followUpFrom || followUpTo)
  const today = localDay()
  // Due means a follow-up on or before today, narrowed further by "to" if earlier.
  const effectiveFollowUpTo = view === 'DUE' && (!followUpTo || followUpTo > today) ? today : followUpTo
  const clearFilters = () => {
    setSearch('')
    setOwner(null)
    setReason('')
    setFollowUpFrom('')
    setFollowUpTo('')
  }

  const query = React.useMemo<NeedsWorkListQuery>(() => ({
    page,
    size: pageSize,
    status,
    ...(ownerId ? { ownerId } : {}),
    ...(reason ? { reason } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(followUpFrom ? { followUpFrom } : {}),
    ...(effectiveFollowUpTo ? { followUpTo: effectiveFollowUpTo } : {}),
  }), [page, pageSize, status, ownerId, reason, debouncedSearch, followUpFrom, effectiveFollowUpTo])

  const { data, isLoading, isFetching, isError } = useGetNeedsWorkListQuery(query, { refetchOnFocus: true })

  // A narrowed list starts over from its first page.
  React.useEffect(() => {
    setPage(0)
  }, [view, ownerId, reason, debouncedSearch, followUpFrom, followUpTo])

  if (isLoading) {
    return <BillingLoading />
  }

  const items: NeedsWorkListItem[] = data?.items ?? []

  return (
    <div>
      <div css={filtersCss}>
        <div>
          <label htmlFor="needs-work-status" css={labelCss}>Status</label>
          <select
            id="needs-work-status"
            className="form-control"
            value={view}
            onChange={(e) => setView(e.target.value as StatusView)}>
            <option value="OPEN">Open</option>
            <option value="DUE">Open — due or overdue</option>
            <option value="RESOLVED">Resolved</option>
          </select>
        </div>
        <div>
          <label htmlFor="needs-work-search" css={labelCss}>Search</label>
          <input
            id="needs-work-search"
            className="form-control"
            value={search}
            placeholder="Patient, charge or order"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label css={labelCss}>Owner</label>
          <OwnerSelect value={owner} onChange={setOwner} isClearable placeholder="Anyone" leadingOptions={me} />
        </div>
        <div>
          <label htmlFor="needs-work-reason" css={labelCss}>Reason</label>
          <select
            id="needs-work-reason"
            className="form-control"
            value={reason}
            onChange={(e) => setReason(e.target.value as NeedsWorkReason | '')}>
            <option value="">Any</option>
            {NEEDS_WORK_REASON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="needs-work-follow-up-from" css={labelCss}>Follow-up from</label>
          <input
            id="needs-work-follow-up-from"
            type="date"
            className="form-control"
            value={followUpFrom}
            max={followUpTo || undefined}
            onChange={(e) => setFollowUpFrom(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="needs-work-follow-up-to" css={labelCss}>Follow-up to</label>
          <input
            id="needs-work-follow-up-to"
            type="date"
            className="form-control"
            value={followUpTo}
            min={followUpFrom || undefined}
            onChange={(e) => setFollowUpTo(e.target.value)}
          />
        </div>
      </div>

      <div className="d-flex align-items-center" css={{ margin: '16px 0 8px', gap: 12, fontSize: 13, color: '#6c757d' }}>
        <span>
          {data?.totalItems ?? 0} {VIEW_COUNT_LABELS[view]} item{(data?.totalItems ?? 0) === 1 ? '' : 's'}
        </span>
        {filtered && (
          <Button variant="link" size="sm" className="p-0" css={{ fontSize: 13 }} onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <div className="ml-auto">
          <Dropdown
            additionalText="Records"
            buttonTypeClass="btn-outline-primary"
            initialValue={String(pageSize)}
            onClick={(value) => {
              setPage(0)
              setPageSize(Number(value))
            }}
            options={PAGE_SIZE_OPTIONS}
          />
        </div>
      </div>

      <div className="table-responsive" css={{ opacity: isFetching ? 0.6 : 1 }}>
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Charge</th>
              <th>Reason</th>
              <th>Owner</th>
              {status === 'OPEN' ? (
                <>
                  <th>Follow-up</th>
                  <th>Time since flagged</th>
                </>
              ) : (
                <>
                  <th>Resolved</th>
                  <th>Closing note</th>
                </>
              )}
              <th />
            </tr>
          </thead>
          <tbody>
            {isError && (
              <tr>
                <td colSpan={7} className="text-center text-danger py-4">
                  The Needs Work list could not be loaded — try again, or tell support if it keeps failing
                </td>
              </tr>
            )}
            {!isError && items.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  {view === 'RESOLVED' ? 'No resolved items' : view === 'DUE' ? 'Nothing is due' : 'Nothing needs work'}
                </td>
              </tr>
            )}
            {!isError && items.length > 0 && (
              items.map((item) => <Row key={item.id} item={item} actions={actions} />)
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        className="pagination-bar"
        currentPage={page}
        totalCount={data?.totalItems || 1}
        pageSize={pageSize}
        siblingCount={1}
        setPageSize={setPageSize}
        onPageChange={setPage}
      />
      {actions.modals}
    </div>
  )
}

export const NeedsWorkListPage = () => (
  <div>
    <DashboardContent
      title="Needs Work"
      icon="fa fa-flag"
      content={(
        <div className="card card-primary mt-1" css={{ padding: '20px 24px' }}>
          <NeedsWorkList />
        </div>
      )}
    />
  </div>
)
